/**
 * Práce s adresami firem — popisky a porovnání s ARESem.
 *
 * Adresy se vybírají na několika místech (objednávka, karta zákazníka, faktura)
 * a všude mají vypadat stejně. Hlavně musí být na první pohled poznat, která
 * z nich je sídlo — to je ta, co patří do fakturační adresy, a u firmy
 * s víc adresami to z holého „Pod šancemi 444/1, Praha" nepoznáš.
 */

/** `address_type` z `crm/enums`; natvrdo, protože se mění jednou za nikdy. */
export const TYPY_ADRES = {
  Main: 'Sídlo',
  Billing: 'Fakturační',
  Shipping: 'Doručovací',
  Subsidiary: 'Pobočka',
  Shop: 'Prodejna',
  Other: 'Jiná',
};

export const TYP_SIDLO = 'Main';

/** Pořadí podle `address_type.sort_order` — sídlo první. */
const PORADI_TYPU = ['Main', 'Billing', 'Shipping', 'Subsidiary', 'Shop', 'Other'];

/** Samotná adresa bez typu: „Nové sady 988/2, Brno, 60200". */
export function adresaText(addr) {
  if (!addr) return '';
  const casti = [addr.street, addr.city, addr.postal_code || addr.zip].filter(Boolean);
  return casti.join(', ') || `Adresa #${addr.id}`;
}

/** Popisek do výběru: „Sídlo — Nové sady 988/2, Brno, 60200". */
export function popisAdresy(addr) {
  if (!addr) return '';
  const typ = TYPY_ADRES[addr.type];
  const text = adresaText(addr);
  return typ ? `${typ} — ${text}` : text;
}

/** Adresy seřazené tak, aby sídlo bylo první (jinak pořadí z API). */
export function serazeneAdresy(adresy = []) {
  return [...adresy].sort((a, b) => {
    const poradiA = PORADI_TYPU.indexOf(a.type);
    const poradiB = PORADI_TYPU.indexOf(b.type);
    return (poradiA === -1 ? 99 : poradiA) - (poradiB === -1 ? 99 : poradiB);
  });
}

/**
 * Srovnání textu pro porovnání s ARESem.
 *
 * Porovnávat natvrdo nejde — ARES píše PSČ bez mezery, my s mezerou, a město
 * bývá jednou „Praha" a jindy „Praha 4". Proto pryč s diakritikou, mezerami
 * a interpunkcí; zůstanou jen písmena a číslice.
 */
function srovnej(hodnota) {
  return String(hodnota || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

/**
 * Odpovídá uložená adresa tomu, co o sídle tvrdí ARES?
 *
 * Rozhoduje ulice s číslem a PSČ — město je u velkých měst zapsané různě
 * („Praha" vs. „Praha 4") a samo o sobě by shodu zbytečně rozbíjelo.
 */
export function odpovidaAresu(addr, ares) {
  if (!addr || !ares) return false;
  const ulice = srovnej(addr.street);
  const psc = srovnej(addr.postal_code || addr.zip);
  if (ulice === '' || srovnej(ares.street) === '') return false;
  if (ulice !== srovnej(ares.street)) return false;
  // PSČ porovnáváme, jen když ho máme na obou stranách.
  return psc === '' || srovnej(ares.postal_code) === '' || psc === srovnej(ares.postal_code);
}

/** Adresa z ARESu jedním řádkem, pro hlášku „Podle ARESu je sídlo …". */
export function aresJednimRadkem(ares) {
  if (!ares) return '';
  const casti = [ares.street, ares.city, ares.postal_code].filter(Boolean);
  return casti.join(', ');
}
